import { AxiosError, AxiosInstance } from 'axios';

import { TokenService } from '@services';
import { RefreshTokenRenewalController } from './utils';
import type {
  ApiErrorDataType,
  AxiosErrorResponseType,
  RefreshTokenRenewalControllerRequestType,
} from './types';

/**
 * Refresh token renewal controller
 * Intercepts requests which failed because of token expiration
 * and tries to renew the token. Also allows to renew token manually
 * @param {AxiosInstance} axios
 * @param {RefreshTokenInterceptorRequestType} request
 */
export function createRefreshTokenRenewalController(
  axios: AxiosInstance,
  request: RefreshTokenRenewalControllerRequestType,
) {
  return new RefreshTokenRenewalController(
    axios,
    async (failedRequest?: AxiosError<ApiErrorDataType>) => {
      try {
        const tokens = await TokenService.get();

        if (!tokens) {
          return Promise.reject(failedRequest);
        }

        const res = await request({
          token: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        });

        await TokenService.set(
          {
            accessToken: res.data.token,
            refreshToken: res.data.refreshToken,
            refreshTokenExpiryTime: res.data.refreshTokenExpiryTime,
          },
          {
            sessionOnly: !TokenService.isRememberMe(),
          },
        );

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    {
      // Specifying token status codes that the interceptor will run for
      shouldRenew: (err: AxiosErrorResponseType) =>
        err?.response?.data.errorCode === 95, // 95 - token expired
    },
  );
}
